import React, { FC, useCallback, useMemo, useState } from 'react'
import { Button, message, Space, Typography } from 'antd'
import { ShoppingCartOutlined, StarFilled } from '@ant-design/icons'
import { usePaddle } from '../../../hooks/usePaddleBilling'
import useBillingAPI from '../../../hooks/useBillingAPI'
import { usePaymentMethod, useShopping } from '../hooks'
import { getPricePeriod, hasConflictingBillingPeriod, hasConflictingProducts } from '../utils'
import { InvoiceRequestFormData, InvoiceRequestModal } from 'molecules/InvoiceRequestModal'
import { AccountPublic, BillingSubscription, CountryChoice, Organization } from '@pollination-solutions/pollination-sdk'
import { useAuth } from 'auth-context'
import { PaymentMethodToggle } from './PaymentMethodToggle'

const { Text } = Typography

interface CheckoutProps {
  account: AccountPublic
  existingSubscriptions: BillingSubscription[]
  onSuccess?: () => void
}

// TODO: This component is not really optimized for maintenance. 
// Consider refactoring in the future.
export const Checkout: FC<CheckoutProps> = ({
  account,
  existingSubscriptions,
  onSuccess
}) => {
  const { openCheckout, isInitializing, initError, isReady } = usePaddle()
  const { loading, sendInvoiceRequestConfirmation } = useBillingAPI()
  const { totalAmount, chart, setChart } = useShopping()
  const { user } = useAuth()
  
  const { 
    paymentMethod,
    billingPeriod,
  } = usePaymentMethod()

  const [showInvoiceModal, setShowInvoiceModal] = useState(false)

  const selectedProducts = useMemo(() => {
    return Object.values(chart)
      // Only valid products with quantity > 0
      ?.filter(item => item.quantity > 0 && item.isValid) ?? []
  }, [chart])

  // const validationResults = useMemo(() => {
  //   const billingPeriodConflict = hasConflictingBillingPeriod(billingPeriod, existingSubscriptions)
  //   const productConflicts = hasConflictingProducts(selectedProducts?.map(item => item.product), existingSubscriptions)

  //   const res = {
  //     billingPeriodConflict,
  //     productConflicts,
  //     hasAnyConflict: billingPeriodConflict.hasConflict || productConflicts.hasConflict
  //   }

  //   return res
  // }, [billingPeriod, selectedProducts, existingSubscriptions])

  const canSubmit = totalAmount > 0 
    && !loading 
    && (paymentMethod === 'invoice' || isReady) //&& !validationResults.hasAnyConflict

  const handleSubmit = useCallback(async () => {
    try {
      // Validate that all selected products have the same billing period
      const products = selectedProducts.map(item => item.product)
      const billingPeriods = products.map(item => getPricePeriod(item.price))
      const uniquePeriods = new Set(billingPeriods)

      if (uniquePeriods.size > 1) {
        message.error('Cannot checkout with mixed billing periods. Please select only monthly or yearly prices.')
        return
      }

      // Check for subscription conflicts
      // if (validationResults.billingPeriodConflict.hasConflict) {
      //   const conflictingSub = validationResults.billingPeriodConflict.conflictingSubscription
      //   message.error(`You already have a ${billingPeriod} subscription (ID: ${conflictingSub?.id?.substring(0, 8)}...). Please cancel it before creating a new ${billingPeriod} subscription.`)
      //   return
      // }

      // if (validationResults.productConflicts.hasConflict) {
      //   const conflictingProductsList = validationResults.productConflicts.conflictingProducts.join(', ')
      //   message.error(`You already have active subscriptions for: ${conflictingProductsList}. Please cancel existing subscriptions for these products before creating new ones.`)
      //   return
      // }

      if (selectedProducts.length === 0) {
        console.error('No products selected')
        return
      }

      // Route to appropriate payment method
      if (paymentMethod === 'invoice') {
        // Open invoice request modal
        setShowInvoiceModal(true)
      } else {
        // Prepare checkout items in the format Paddle expects
        const checkoutItems = selectedProducts.map(item => ({
          priceId: item.product.price.id,
          quantity: item.quantity
        }))

        // Call the Paddle checkout
        openCheckout({
          url: window.location.href,
          items: checkoutItems,
          account,
          onSuccess: (_data, err) => {
            if (err) {
              console.error('Paddle checkout error:', err)
              return
            }
            setChart({})
            onSuccess?.()
          }
        })
      }
    } catch (error) {
      console.error('Failed to start checkout:', error)
    }
  }, [selectedProducts, account, openCheckout, onSuccess, billingPeriod, paymentMethod]) // validationResults

  const handleInvoiceRequest = useCallback(async (
    formData: InvoiceRequestFormData, 
    _organization: Organization | null) => {
    try {
      // Prepare the request data with selected products in the format expected by the SDK
      const additionalEmails = formData.additional_contact_email
        ? [formData.additional_contact_email]
        : []

      const requestData = {
        account: account.name,
        address_line1: formData.billing_address,
        city: formData.city,
        region: formData.state,
        postal_code: formData.postal_code,
        country: formData.country as CountryChoice,
        items: selectedProducts.map(item => ({
          product_id: item.product.product.id,
          price_id: item.product.price.id,
          quantity: item.quantity
        })),
        additional_emails: additionalEmails
      }

      await sendInvoiceRequestConfirmation(requestData)

      // Clear selected products and close modal on success
      setChart({})
      setShowInvoiceModal(false)
      onSuccess?.()
    } catch (error) {
      // Error is handled in the useBillingAPI hook
      console.error('Failed to submit invoice request:', error)
    }
  }, [selectedProducts, account, sendInvoiceRequestConfirmation, onSuccess])

  return (
    <Space 
      direction='vertical' 
      style={{ width: '100%', textAlign: 'center' }} 
      size={'large'}
    >
      <PaymentMethodToggle />

      <Button
        type="primary"
        size="large"
        loading={loading || isInitializing}
        disabled={!canSubmit}
        onClick={handleSubmit}
        icon={<ShoppingCartOutlined />}
        style={{
          height: '50px',
          fontSize: '16px',
          fontWeight: 'bold',
          paddingLeft: '32px',
          paddingRight: '32px'
        }}
      >
        {isInitializing
          ? 'Loading Checkout...'
          : paymentMethod === 'invoice'
            ? 'Request Invoice'
            : 'Proceed to Checkout'
        }
      </Button>

      {initError && (
        <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
          <Text type="danger" style={{ fontSize: '12px' }}>
            ⚠️ Checkout unavailable: {initError}
          </Text>
        </div>
      )}

      {canSubmit && !initError && (
        <div style={{ marginTop: '8px' }}>
          {paymentMethod === 'invoice' ? (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              📧 You&apos;ll receive a confirmation email and staff will contact you within a few business days
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <StarFilled style={{ color: '#faad14' }} /> Secure checkout powered by Paddle
            </Text>
          )}
        </div>
      )}

      {/* {validationResults.hasAnyConflict && (
        <div style={{ marginTop: '8px' }}>
          <Text type="danger" style={{ fontSize: '12px' }}>
            ⚠️ Please resolve conflicts above before proceeding
          </Text>
        </div>
      )} */}

      {totalAmount === 0 && !initError  && (
        <div style={{ marginTop: '8px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Please select at least one product to continue
          </Text>
        </div>
      )} 
      {/* && !validationResults.hasAnyConflict */}

    {/* Invoice Request Modal */}
    {user && (
      <InvoiceRequestModal
        visible={showInvoiceModal}
        user={user}
        account={account}
        onClose={() => setShowInvoiceModal(false)}
        onSubmit={handleInvoiceRequest}
        loading={loading}
        selectedProducts={selectedProducts.map(item => ({
          product: item.product.product,
          price: item.product.price,
          quantity: item.quantity
        }))}
        totalAmount={totalAmount}
        currency={selectedProducts[0]?.product.price.unit_price.currency_code || 'USD'}
      />
    )}
    </Space>
  )
}
